import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSurveyCompleted } from '../../state/actions/surveyActions';
import * as constants from './constants';
import IntroStep from './steps/IntroStep';
import MultiChoiceQuestionStep from './steps/MultiChoiceQuestionStep';
import MultiSelectQuestionStep from './steps/MultiSelectQuestionStep';
import { FormProgressBar } from '../shared/FormProgressBar';
import { connect } from 'react-redux';
import { ENV } from '../../constants/environments';
import { ApplicationState } from "../../types/state/storeTypes";
import { User } from '../../types/api/UsersTypes';
import { UserSurveyProps } from '../../types/components/user-survey/UserSurveyFlow';
import {
  getNextStep,
  getPreviousStep,
  getQuestionPrompt,
  submitSurveyAnswers
} from './helpers'
import { Loader } from '../shared/Loader';
import { AnswerConfig } from '../../types/components/user-survey/shared/Answer';

export const containerStyles = "p-4 flex flex-col gap-4 lg:gap-8 max-w-2xl overflow-visible lg:w-[860px]"

export const progressBarBgColor = "bg-gray-700"
export const progressBarClassName = "bg-[#00A1E0] h-full rounded-full transition-all duration-300 ease-out"

export const contentContainerStyles = "transition-all duration-700 ease-in transition-discrete"

export const errorTextStyle = "text-center text-black-friday-banner-red-default"


interface SurveyAnswersMap {
  [key: number]: AnswerConfig[];
}

const UserSurveyFlow: React.FC<UserSurveyProps> = ({ user, onFlowCompleted }) => {
  const [currentStep, setCurrentStep] = useState<number>(constants.INTRO);
  const [answers, setAnswers] = useState<SurveyAnswersMap>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const apiUrl = ENV.api.baseURL;

  const handleAnswerSelected = (stepIndex: number, answer: AnswerConfig) => {
    setAnswers(prev => ({ ...prev, [stepIndex]: [answer] }));
  };

  const handleMultiAnswersSelected = (stepIndex: number, selectedAnswers: AnswerConfig[]) => {
    setAnswers(prev => ({ ...prev, [stepIndex]: selectedAnswers }));
  };

  const nextStep = () => {
    if (currentStep === constants.HOW_INQUIRY_STEP) {
      submitSurveyAnswers(answers, apiUrl, user.token, setLoading, setError, dispatch)
        .then(success => {
          if (!success) return;
          dispatch(setSurveyCompleted());
          if (!onFlowCompleted) return;
          onFlowCompleted();
        });
      return;
    }

    setCurrentStep(getNextStep(currentStep));
  };

  const previousStep = () => {
    setCurrentStep(getPreviousStep(currentStep));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case constants.INTRO:
        return <IntroStep handleNext={nextStep} />;
      case constants.ROLE_QUESTION_STEP:
      case constants.EXPERIENCE_QUESTION_STEP:
      case constants.HOW_INQUIRY_STEP:
        return (
          <MultiChoiceQuestionStep
            key={`step-${currentStep}`}
            question={getQuestionPrompt(currentStep)}
            stepIndex={currentStep}
            currentAnswer={answers[currentStep] ? answers[currentStep][0] : undefined}
            onAnswerSelected={(answer) => handleAnswerSelected(currentStep, answer)}
            onBack={previousStep}
            onNext={nextStep}
          />
        );
      case constants.SOFTWARE_QUESTION_STEP:
        return (
          <MultiSelectQuestionStep
            key={`step-${currentStep}`}
            question={constants.SOFTWARE_QUESTION_PROMPT}
            stepIndex={currentStep}
            currentAnswers={answers[currentStep] as AnswerConfig[] | undefined}
            onAnswersSelected={(selectedAnswers) =>
              handleMultiAnswersSelected(currentStep, selectedAnswers)
            }
            onBack={previousStep}
            onNext={nextStep}
          />
        );
      default:
        return <div>Invalid step: {currentStep}</div>;
    }
  };

  return (
    <div cy-test-id="user-survey-flow" className={containerStyles}>
      {currentStep > 0 && (
        <FormProgressBar
          bgColor={progressBarBgColor}
          className={progressBarClassName}
          progressPct={(currentStep / 4) * 100}
          cornered={true}
        />
      )}
      <div className={contentContainerStyles}>
        {error && <p className={errorTextStyle}>An error has occurred. Please try again later.</p>}
        {loading && <Loader />}
        {renderCurrentStep()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: ApplicationState): any => ({
  user: state.auth.user as User
});

export default connect(mapStateToProps)(UserSurveyFlow);
